import { DomainError } from '../errors/domain-error';

export const COMMUNITY_POST_AUDIO_MAX_BYTES = 25 * 1024 * 1024;
export const COMMUNITY_POST_AUDIO_MAX_DURATION_SEC = 300;

export const COMMUNITY_POST_AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp4',
  'audio/webm',
  'audio/wav',
  'audio/ogg',
] as const;

export const ALLOWED_GIF_PROVIDER_HOSTS = [
  'media.tenor.com',
  'media1.tenor.com',
  'c.tenor.com',
  'media.giphy.com',
  'media0.giphy.com',
  'media1.giphy.com',
  'media2.giphy.com',
  'media3.giphy.com',
  'media4.giphy.com',
  'i.giphy.com',
] as const;

/** Static sticker catalog — URLs must match validateAllowlistedStickerUrl. */
export const COMMUNITY_STICKER_CATALOG = [
  {
    id: 'wave',
    label: 'Wave',
    url: 'https://media.tenor.com/9Zlg9aG05-AAAAAC/thumbs-up.gif',
  },
  {
    id: 'celebrate',
    label: 'Celebrate',
    url: 'https://media.tenor.com/gUiu1zyxfzYAAAAj/dance-dancing.gif',
  },
  {
    id: 'clap',
    label: 'Clap',
    url: 'https://media.tenor.com/1E2t5uicwAAAAAj/clap-clapping.gif',
  },
  {
    id: 'fire',
    label: 'Fire',
    url: 'https://media.tenor.com/On7kvXhzmlUAAAAj/ah-yeah.gif',
  },
  {
    id: 'heart',
    label: 'Heart',
    url: 'https://media.tenor.com/_ADeXfjD0-0AAAAj/heart-love.gif',
  },
  {
    id: 'star',
    label: 'Star',
    url: 'https://media.tenor.com/3hX0qGj4Y9wAAAAj/star.gif',
  },
] as const;

export const ALLOWED_STICKER_URLS = COMMUNITY_STICKER_CATALOG.map((sticker) => sticker.url);

export type GifSearchResult = {
  id: string;
  title: string;
  previewUrl: string;
  url: string;
  width: number;
  height: number;
};

export function isAllowlistedProviderGifUrl(url: string): boolean {
  try {
    const parsed = new URL(url.trim());
    if (parsed.protocol !== 'https:') return false;
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    return ALLOWED_GIF_PROVIDER_HOSTS.includes(host as (typeof ALLOWED_GIF_PROVIDER_HOSTS)[number]);
  } catch {
    return false;
  }
}

export function validateAllowlistedProviderGifUrl(
  url?: string | null,
): { ok: true; value: string } | { ok: false; message: string } {
  const trimmed = url?.trim();
  if (!trimmed) {
    return { ok: false, message: 'GIF URL is required' };
  }
  if (!isAllowlistedProviderGifUrl(trimmed)) {
    return { ok: false, message: 'GIF must come from an approved provider' };
  }
  return { ok: true, value: trimmed };
}

export function isAllowlistedStickerUrl(url: string): boolean {
  const trimmed = url.trim();
  return ALLOWED_STICKER_URLS.some((allowed) => allowed === trimmed);
}

export function validateAllowlistedStickerUrl(
  url?: string | null,
): { ok: true; value: string } | { ok: false; message: string } {
  const trimmed = url?.trim();
  if (!trimmed) {
    return { ok: false, message: 'Sticker URL is required' };
  }
  if (!isAllowlistedStickerUrl(trimmed)) {
    return { ok: false, message: 'Sticker must come from the approved catalog' };
  }
  return { ok: true, value: trimmed };
}

export function validateAllowlistedCommunityImageUrl(
  url?: string | null,
): { ok: true; value: string; kind: 'gif' | 'sticker' } | { ok: false; message: string } {
  const gif = validateAllowlistedProviderGifUrl(url);
  if (gif.ok) return { ...gif, kind: 'gif' };
  const sticker = validateAllowlistedStickerUrl(url);
  if (sticker.ok) return { ...sticker, kind: 'sticker' };
  return { ok: false, message: 'Image must come from an approved GIF provider or sticker catalog' };
}

export function validateCommunityPostAudioMimeType(
  mimeType?: string | null,
): { ok: true } | { ok: false; message: string } {
  const normalized = mimeType?.trim().toLowerCase();
  if (!normalized) {
    return { ok: false, message: 'Audio mime type is required' };
  }
  if (!COMMUNITY_POST_AUDIO_MIME_TYPES.includes(normalized as (typeof COMMUNITY_POST_AUDIO_MIME_TYPES)[number])) {
    return { ok: false, message: 'Unsupported audio format' };
  }
  return { ok: true };
}

export function validateCommunityPostAudioDuration(
  durationSeconds?: number | null,
): { ok: true } | { ok: false; message: string } {
  if (durationSeconds == null || !Number.isFinite(durationSeconds)) {
    return { ok: false, message: 'Audio duration is required' };
  }
  if (durationSeconds <= 0) {
    return { ok: false, message: 'Audio duration is invalid' };
  }
  if (durationSeconds > COMMUNITY_POST_AUDIO_MAX_DURATION_SEC) {
    return {
      ok: false,
      message: `Audio must be ${COMMUNITY_POST_AUDIO_MAX_DURATION_SEC} seconds or shorter`,
    };
  }
  return { ok: true };
}

export function assertCommunityPostMediaExclusivity(params: {
  type?: string | null;
  hasGif?: boolean;
  hasUploadedMedia?: boolean;
  hasAttachments?: boolean;
  hasPoll?: boolean;
  hasLink?: boolean;
}): void {
  if (params.type === 'audio') {
    if (params.hasPoll || params.hasLink || params.hasAttachments || params.hasGif) {
      throw new DomainError('Audio posts cannot include other attachments');
    }
    if (!params.hasUploadedMedia) {
      throw new DomainError('Audio posts require an uploaded audio file');
    }
  }

  if (params.hasGif && (params.hasUploadedMedia || params.hasPoll || params.hasLink || params.hasAttachments)) {
    throw new DomainError('GIF posts cannot include other media attachments');
  }
}
